import React from "react";
import { Link } from "react-router-dom";
import "./WhyDigicomm.css";

interface WhyDigicommItem {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

/* =========================================================
   CARD ICONS
========================================================= */

const EngineeringIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <circle cx="32" cy="32" r="9" />

    <path d="M32 8v11M32 45v11M8 32h11M45 32h11" />

    <path d="M15 15l8 8M41 41l8 8M49 15l-8 8M23 41l-8 8" />

    <path d="m32 24 3 4 5 1-3 4 .5 5-5.5-2-5.5 2 .5-5-3-4 5-1z" />
  </svg>
);

const SemiconductorIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="17" y="17" width="30" height="30" rx="3" />

    <rect x="24" y="24" width="16" height="16" rx="2" />

    <path d="M23 8v9M32 8v9M41 8v9" />

    <path d="M23 47v9M32 47v9M41 47v9" />

    <path d="M8 23h9M8 32h9M8 41h9" />

    <path d="M47 23h9M47 32h9M47 41h9" />
  </svg>
);

const CollaborationIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <circle cx="32" cy="19" r="8" />

    <circle cx="15" cy="27" r="6" />

    <circle cx="49" cy="27" r="6" />

    <path d="M20 51v-6c0-7 5-12 12-12s12 5 12 12v6" />

    <path d="M5 50v-4c0-6 4-10 10-10 4 0 7 2 9 5" />

    <path d="M59 50v-4c0-6-4-10-10-10-4 0-7 2-9 5" />
  </svg>
);

const DeliveryIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <path d="m7 27 12-9 11 8-8 8z" />

    <path d="m57 27-12-9-11 8 8 8z" />

    <path d="M22 34l7 7c2 2 5 2 7 0l6-7" />

    <path d="m18 21 6-7h9l6 7" />

    <path d="m16 37 8 8M22 32l13 13M48 37l-8 8" />
  </svg>
);

const OutcomeIcon = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <path d="M32 7 52 15v15c0 13-8 22-20 27C20 52 12 43 12 30V15z" />

    <path d="m22 32 7 7 14-16" />
  </svg>
);

/* =========================================================
   VISUAL ICONS
========================================================= */

const MiniChipIcon = () => (
  <svg viewBox="0 0 40 40">
    <rect x="10" y="10" width="20" height="20" rx="3" />
    <rect x="15" y="15" width="10" height="10" rx="1" />

    <path d="M15 4v6M20 4v6M25 4v6" />
    <path d="M15 30v6M20 30v6M25 30v6" />

    <path d="M4 15h6M4 20h6M4 25h6" />
    <path d="M30 15h6M30 20h6M30 25h6" />
  </svg>
);

const MiniVerifyIcon = () => (
  <svg viewBox="0 0 40 40">
    <path d="M20 5 33 10v10c0 8-5 13-13 17C12 33 7 28 7 20V10z" />

    <path d="m14 20 4 4 8-9" />
  </svg>
);

const MiniTeamIcon = () => (
  <svg viewBox="0 0 40 40">
    <circle cx="20" cy="13" r="5" />

    <circle cx="9" cy="17" r="4" />

    <circle cx="31" cy="17" r="4" />

    <path d="M11 34c1-7 4-11 9-11s8 4 9 11" />

    <path d="M2 33c1-5 3-8 7-8" />

    <path d="M38 33c-1-5-3-8-7-8" />
  </svg>
);

const MiniTargetIcon = () => (
  <svg viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="14" />
    <circle cx="20" cy="20" r="8" />
    <circle cx="20" cy="20" r="2" />

    <path d="M23 17 35 5" />
    <path d="M29 5h6v6" />
  </svg>
);

/* =========================================================
   CONTENT
========================================================= */

const whyDigicommItems: WhyDigicommItem[] = [
  {
    number: "01",
    title: "Engineering-First Approach",
    description:
      "Every engagement is driven by engineering discipline, technical excellence, and structured execution.",
    icon: <EngineeringIcon />,
  },

  {
    number: "02",
    title: "Specialized Semiconductor Expertise",
    description:
      "Focused expertise across core semiconductor engineering disciplines, supported by specialized capabilities and flexible engagement models that address the evolving needs of semiconductor product development.",
    icon: <SemiconductorIcon />,
  },

  {
    number: "03",
    title: "Collaborative Delivery",
    description:
      "Engineering teams work seamlessly with customer organizations through transparent communication and shared execution ownership.",
    icon: <CollaborationIcon />,
  },

  {
    number: "04",
    title: "Commitment to Reliable Outcomes",
    description:
      "Consistent focus on quality, predictability, and dependable engineering delivery across every engagement.",
    icon: <OutcomeIcon />,
  },

  {
    number: "05",
    title: "Dependable Engineering Extension",
    description:
      "We work as an extension of your product development team, bringing ownership, accountability, and disciplined execution.",
    icon: <DeliveryIcon />,
  },
];

/* =========================================================
   COMPONENT
========================================================= */

const WhyDigicomm: React.FC = () => {
  return (
    <section className="why-digicomm">
      {/* BACKGROUND */}
      <div className="why-digicomm__glow why-digicomm__glow--one" />
      <div className="why-digicomm__glow why-digicomm__glow--two" />

      <div className="container-fluid">
        <div className="why-digicomm__wrapper mlr-20">

          {/* =================================================
              TOP SECTION
          ================================================= */}

          <div className="why-digicomm__top">

            {/* LEFT CONTENT */}
            <div className="why-digicomm__content">

              {/* <div className="why-digicomm__section-label">
                <span>SECTION 8</span>

                <b aria-hidden="true">
                  <i />
                  <i />
                  <i />
                </b>
              </div> */}

              <h2 className="why-digicomm__title">
                Why <span>Digicomm?</span>
              </h2>

              <span className="why-digicomm__title-line" />

              <p className="why-digicomm__description">
                Semiconductor engineering demands more than technical
                capability—it requires structured execution, engineering
                ownership, and the ability to deliver consistently across every
                stage of the silicon development lifecycle. Digicomm brings
                together specialized expertise, collaborative engagement, and
                disciplined delivery practices to become a dependable
                engineering extension of your product development team.
              </p>

            </div>

            {/* =================================================
                CREATIVE SEMICONDUCTOR VISUAL
            ================================================= */}

            <div className="why-digicomm__visual" aria-hidden="true">

              {/* background grid */}
              <div className="why-digicomm__visual-grid" />

              {/* scanning beam */}
              <div className="why-digicomm__scan" />

              {/* crosshair */}
              <div className="why-digicomm__crosshair">
                <span />
                <span />
              </div>

              {/* orbit rings */}
              <div className="why-digicomm__orbit why-digicomm__orbit--outer">
                <i />
              </div>

              <div className="why-digicomm__orbit why-digicomm__orbit--middle">
                <i />
              </div>

              <div className="why-digicomm__orbit why-digicomm__orbit--inner">
                <i />
              </div>

              {/* circuit routes */}
              <div className="why-digicomm__routes">
                <span className="route route--1" />
                <span className="route route--2" />
                <span className="route route--3" />
                <span className="route route--4" />
              </div>

              {/* ENGINEERING NODE */}
              <div className="why-digicomm__node why-digicomm__node--one">
                <div className="why-digicomm__node-icon">
                  <MiniChipIcon />
                </div>

                <span>Engineering</span>
              </div>

              {/* VERIFICATION NODE */}
              <div className="why-digicomm__node why-digicomm__node--two">
                <div className="why-digicomm__node-icon">
                  <MiniVerifyIcon />
                </div>

                <span>Reliability</span>
              </div>

              {/* COLLABORATION NODE */}
              <div className="why-digicomm__node why-digicomm__node--three">
                <div className="why-digicomm__node-icon">
                  <MiniTeamIcon />
                </div>

                <span>Collaboration</span>
              </div>

              {/* DELIVERY NODE */}
              <div className="why-digicomm__node why-digicomm__node--four">
                <div className="why-digicomm__node-icon">
                  <MiniTargetIcon />
                </div>

                <span>Delivery</span>
              </div>

              {/* CENTRAL SILICON */}
              <div className="why-digicomm__silicon">

                <div className="why-digicomm__silicon-glow" />

                <div className="why-digicomm__silicon-base">
                  <span className="why-digicomm__silicon-pin pin-top" />
                  <span className="why-digicomm__silicon-pin pin-right" />
                  <span className="why-digicomm__silicon-pin pin-bottom" />
                  <span className="why-digicomm__silicon-pin pin-left" />

                  <div className="why-digicomm__silicon-layer">
                    <div className="why-digicomm__silicon-core">
                      <MiniChipIcon />

                      {/* <span>DIGICOMM</span>

                      <small>
                        ENGINEERING
                      </small>*/}
                    </div>
                  </div>
                </div>

              </div>

              {/* bottom status */}
              <div className="why-digicomm__status">

                <span className="why-digicomm__status-dot" />

                <span>
                  ENGINEERING ECOSYSTEM
                </span>

                <i />

                <strong>
                  ACTIVE
                </strong>

              </div>

            </div>

          </div>

          {/* =================================================
              SUB HEADING
          ================================================= */}

          <div className="why-digicomm__heading-row">

            <span className="why-digicomm__heading-line" />

            <h3>
              <span>What Sets</span> Digicomm Apart
            </h3>

            <span className="why-digicomm__heading-line" />

          </div>

          {/* =================================================
              CARDS
          ================================================= */}

          <div className="why-digicomm__items">

            {whyDigicommItems.map((item, index) => (
              <article
                className={`why-digicomm__item ${
                  index % 2 === 0
                    ? "why-digicomm__item--green"
                    : "why-digicomm__item--blue"
                }`}
                key={item.number}
              >

                <div className="why-digicomm__icon">
                  {item.icon}
                </div>

                <span className="why-digicomm__number">
                  {item.number}
                </span>

                <h4>
                  {item.title}
                </h4>

                <span className="why-digicomm__item-line" />

                <p>
                  {item.description}
                </p>

                <strong aria-hidden="true">
                  {item.number}
                </strong>

              </article>
            ))}

          </div>

          {/* =================================================
              CTA
          ================================================= */}

          <div className="why-digicomm__cta-wrap">

            <Link
              to="/about"
              className="why-digicomm__cta"
              aria-label="Learn more about Digicomm"
            >

              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 4h6v6" />

                <path d="m20 4-9 9" />

                <path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
              </svg>

              <span>
                Learn More About Digicomm
              </span>

            </Link>

          </div>

        </div>
      </div>
    </section>
  );
};

export default WhyDigicomm;